import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import ButtonCircular from '@deliveryhero/pd-cookbook/components/ButtonCircular';
import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Divider } from '@deliveryhero/pd-cookbook/components/Divider';
import { Grid } from '@deliveryhero/pd-cookbook/components/Grid';
import IcClose from '@deliveryhero/pd-cookbook/components/Icons/IcClose';
import IcEmail from '@deliveryhero/pd-cookbook/components/Icons/IcEmail';
import IcGroup from '@deliveryhero/pd-cookbook/components/Icons/IcGroup';
import IcProDeliveryTag from '@deliveryhero/pd-cookbook/components/Icons/IcProDeliveryTag';
import { Toast } from '@deliveryhero/pd-cookbook/components/Toast';
import { Tooltip } from '@deliveryhero/pd-cookbook/components/Tooltip';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';

import classNames from 'classnames';
import { Card } from 'components/Card';
import { LoadingAnimation } from 'components/LoadingAnimation';
import Page, { ActionType } from 'components/Page';
import { BrandCodes } from 'components/Theme/types';
import { useConfiguration } from 'contexts/country-config';
import { useFeatureFlags } from 'contexts/feature-flags';
import { format, intervalToDuration, parseISO } from 'date-fns';
import useAction from 'hooks/useAction';
import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import useUser from 'hooks/useUser';
import { SuccessResponse } from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import DashboardQueryKeys from 'src/query-keys/dashboard';
import { formatDatePayload } from 'utils';
import { FeatureFlag } from 'utils/constants';

import { DashboardLabelText } from './constants';
import { OrdersAndExpense } from './OrdersAndExpense';
import { DashboardAction } from './types';

import './styles.scss';

export const DashboardContainer = () => {
  const countryConfig = useConfiguration();
  const countryCode = countryConfig.code.toLowerCase();
  const user = useUser();
  const featureFlags = useFeatureFlags();
  const brandCode = countryConfig.entity.code.toLowerCase() as BrandCodes;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pushNotification } = useNotification();
  const [, setAction] = useAction<DashboardAction>();
  const currency = countryConfig.currencyCode;
  const [datePeriod, setDatePeriod] = useState('0');
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const placeholderData = {
    pending_employees: 0,
    active_employees: 0,
    active_subscription: 0,
    gift_cards_count: 0,
    allowances_count: 0,
    resend_activation_emails_triggered_at: null
  };

  const api = useApi();

  let isDisabled = false;

  const { data, isLoading } = useQueryWrapperP0(
    DashboardQueryKeys.order(datePeriod),
    async () => {
      let startDate: Date;
      let endDate: Date;

      if (datePeriod == '0') {
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
      } else if (datePeriod == '1') {
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
      } else {
        const date = new Date();
        startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
        endDate = new Date(date.getFullYear(), date.getMonth(), 0);
      }

      const response = await api.company.order.getOrdersStats({
        date_from: formatDatePayload(startDate),
        date_to: formatDatePayload(endDate)
      });
      return response.data;
    },
    {
      onError: () => {
        pushNotification('Failed to fetch order stats', {
          variant: 'error',
          autoHideDuration: 5000
        });
      },
      enabled: user.company.is_company_management_enabled
    }
  );

  const dashboardStatsQuery = useQueryWrapperP0(
    DashboardQueryKeys.company(),
    () => api.company.dashboard.getStats(),
    {
      onError: () => {
        pushNotification('Failed to fetch dashboard stats', {
          variant: 'error',
          autoHideDuration: 5000
        });
      },
      enabled: user.company.is_company_management_enabled
    }
  );

  const bulkResendInvitation = useMutation<SuccessResponse, Error, null>(
    async () => {
      return api.company.user.bulkResendInvitation();
    }
  );

  const handleBulkResendInvitationEmail = () => {
    bulkResendInvitation.mutate(null, {
      onSuccess: () => {
        setShowToast(true);
        queryClient.invalidateQueries('Dashboard', { exact: false });
      },
      onError: () => {
        pushNotification('We are unable to send the invites', {
          variant: 'error',
          autoHideDuration: 5000
        });
      }
    });
  };

  useEffect(() => {
    if (!user.company.is_company_management_enabled) {
      navigate(
        {
          pathname: AppRoute.GiftCard
        },
        { replace: true }
      );
    }
  }, [user]);

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.DASHBOARD });
  }, []);

  if (
    dashboardStatsQuery.isLoading ||
    !user.company.is_company_management_enabled
  ) {
    return <LoadingAnimation />;
  }

  const dashboardStats = dashboardStatsQuery.data?.data || placeholderData;

  if (dashboardStats.resend_activation_emails_triggered_at) {
    const interval = intervalToDuration({
      start: parseISO(dashboardStats.resend_activation_emails_triggered_at),
      end: new Date()
    });

    isDisabled =
      interval.days !== undefined && interval.days === 0 && interval.years == 0;
  }

  const ctaColumns = [
    {
      enabled:
        dashboardStats.allowances_count == 0 &&
        user.company.is_allowance_enabled,
      img: 'https://images.deliveryhero.io/image/foodpanda/corporate/gift_card_web/allowances.png',
      titleText: DashboardLabelText.ALLOWANCE_CTA.TITLE,
      infoText: DashboardLabelText.ALLOWANCE_CTA.INFO,
      buttonText: DashboardLabelText.ALLOWANCE_CTA.BUTTON,
      to: AppRoute.Allowance
    },
    {
      enabled:
        brandCode != 'fo' &&
        user.company.is_subscription_enabled &&
        dashboardStats.active_subscription == 0,
      img: 'https://images.deliveryhero.io/image/foodpanda/corporate/gift_card_web/pandapro.png',
      titleText: DashboardLabelText.PANDAPRO_CTA.TITLE,
      infoText: DashboardLabelText.PANDAPRO_CTA.INFO,
      buttonText: DashboardLabelText.PANDAPRO_CTA.BUTTON,
      to: AppRoute.Subscription
    },
    {
      enabled: featureFlags(FeatureFlag.GiftCard),
      img: 'https://images.deliveryhero.io/image/foodpanda/corporate/gift_card_web/giftcards.png',
      titleText: DashboardLabelText.GIFTCARD_CTA.TITLE,
      infoText: DashboardLabelText.GIFTCARD_CTA.INFO,
      buttonText: DashboardLabelText.GIFTCARD_CTA.BUTTON,
      to: AppRoute.GiftCard
    }
  ];

  const isSingleLayout = ctaColumns.filter((c) => c.enabled).length == 1;

  const renderCtaCard = (
    enabled: boolean,
    img: string,
    titleText: string,
    infoText: string,
    buttonText: string,
    to: AppRoute
  ) => {
    if (!enabled) return;
    return (
      <Card
        className={classNames('dashboard-container', {
          'dashboard-container__cta-single': isSingleLayout
        })}
        key={to}
      >
        <img src={img} />
        <div
          className={classNames('dashboard-container', {
            'dashboard-container__cta-single__content': isSingleLayout
          })}
        >
          <Typography
            type="title-sm"
            color="neutral-primary"
            marginLeft="md"
            marginRight="md"
            marginTop="sm"
            marginBottom="xs"
          >
            {titleText}
          </Typography>
          <Typography
            type="label-sm"
            color="neutral-secondary"
            marginLeft="md"
            marginRight="md"
            marginBottom="sm"
          >
            {infoText}
          </Typography>
          <Button
            key="3"
            marginLeft="md"
            marginRight="md"
            marginBottom="sm"
            size="small"
            layout="full-width-secondary"
            type="button"
            onClick={() =>
              navigate(
                {
                  pathname: to
                },
                { replace: true }
              )
            }
          >
            {buttonText}
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <Page
      actions={{
        [DashboardAction.RESEND_ACTION]: {
          component: (
            <>
              <Button
                key="7"
                type="button"
                variant="secondary"
                onClick={() => setAction(DashboardAction.RESEND_ACTION, false)}
              >
                {DashboardLabelText.CONFIRM_MODAL.CANCEL_BUTTON}
              </Button>
              <Button
                key="8"
                type="button"
                onClick={handleBulkResendInvitationEmail}
              >
                {DashboardLabelText.CONFIRM_MODAL.CONFIRM_BUTTON}
              </Button>
            </>
          ),
          type: ActionType.AppDialog,
          options: {
            title: (
              <div className="dialog">
                <div className="dialog__content">
                  <div className="dialog__content__title">
                    <IcEmail style={{ fill: '#FF2B85' }} />
                    <Typography
                      type="title-md"
                      color="neutral-primary"
                      marginLeft="xs"
                    >
                      {DashboardLabelText.CONFIRM_MODAL.TITLE}
                    </Typography>
                  </div>
                  <Typography type="paragraph-sm" color="neutral-primary">
                    {DashboardLabelText.CONFIRM_MODAL.SUBTITLE}
                  </Typography>
                </div>
                <ButtonCircular
                  ariaLabel="close-dialog-button"
                  onClick={() =>
                    setAction(DashboardAction.RESEND_ACTION, false)
                  }
                >
                  <IcClose fill="#E21B70" />
                </ButtonCircular>
              </div>
            ),
            content: (
              <>
                <Typography type="paragraph-md" color="neutral-primary">
                  This will be sent to all {dashboardStats.pending_employees}{' '}
                  employees who haven’t activated their accounts yet
                </Typography>
              </>
            )
          }
        }
      }}
    >
      <Grid
        gridTemplateRows="auto auto auto"
        rowGap="24px"
        className="dashboard-container"
      >
        {/* CTA */}
        <Grid
          gridTemplateColumns={`repeat(auto-fit, minmax(314px, ${
            isSingleLayout ? '1fr' : '314px'
          }))`}
          columnGap="24px"
          className="dashboard-container__cta"
        >
          {ctaColumns.map((column) =>
            renderCtaCard(
              column.enabled,
              column.img,
              column.titleText,
              column.infoText,
              column.buttonText,
              column.to
            )
          )}
        </Grid>
        {/* Stats */}
        <Grid
          className="dashboard-container__stats"
          columnGap="24px"
          gridTemplateColumns="repeat(auto-fit, minmax(314px, 1fr))"
        >
          {dashboardStats.pending_employees > 0 && (
            <Card>
              <Grid
                gridTemplateColumns="auto auto"
                columnGap="16px"
                className="dashboard-container__stats__content"
              >
                <Card className="dashboard-container__stats__icon">
                  <IcEmail style={{ fill: '#56616A' }} />
                </Card>
                <div>
                  <Typography type="label-sm" color="neutral-secondary">
                    {DashboardLabelText.STATS.PENDING_EMPLOYEES}
                  </Typography>
                  <Typography type="title-lg" color="neutral-primary">
                    {dashboardStats.pending_employees}
                  </Typography>
                </div>
              </Grid>
              <Divider />
              <div
                onMouseEnter={() => isDisabled && setIsTooltipOpen(true)}
                onMouseLeave={() => setIsTooltipOpen(false)}
              >
                <Tooltip
                  isVisibleForAnimation={isTooltipOpen}
                  arrowAlignment="left"
                  bodyText={
                    dashboardStats.resend_activation_emails_triggered_at
                      ? `You can send this once every 24 hours. Last sent on ${format(
                          parseISO(
                            dashboardStats.resend_activation_emails_triggered_at
                          ),
                          'MMM d, hh:mm a'
                        )}`
                      : ''
                  }
                  placement="right"
                >
                  <Button
                    key="4"
                    variant="text"
                    layout="full-width-secondary"
                    type="button"
                    disabled={isDisabled}
                    onClick={() => setAction(DashboardAction.RESEND_ACTION)} // add confirmation dialog
                  >
                    {DashboardLabelText.STATS.PENDING_EMPLOYEES_BUTTON}
                  </Button>
                  <Toast
                    show={showToast}
                    message={DashboardLabelText.SUCCESS_TOAST_MESSAGE}
                    onHide={() => setShowToast(false)}
                    showIcon
                    hideDuration={5000}
                  />
                </Tooltip>
              </div>
            </Card>
          )}
          <Card>
            <Grid
              gridTemplateColumns="auto auto"
              columnGap="16px"
              className="dashboard-container__stats__content"
            >
              <Card className="dashboard-container__stats__icon">
                <IcGroup style={{ fill: '#56616A' }} />
              </Card>
              <div>
                <Typography type="label-sm" color="neutral-secondary">
                  {DashboardLabelText.STATS.ACTIVE_EMPLOYEES}
                </Typography>
                <Typography type="title-lg" color="neutral-primary">
                  {dashboardStats.active_employees}
                </Typography>
              </div>
            </Grid>
            <Divider />
            <Button
              key="5"
              variant="text"
              layout="full-width-secondary"
              type="button"
              onClick={() =>
                navigate(
                  {
                    pathname: AppRoute.Employee
                  },
                  { replace: true }
                )
              }
            >
              {DashboardLabelText.STATS.ACTIVE_EMPLOYEES_BUTTON}
            </Button>
          </Card>
          {user.company.is_subscription_enabled &&
            dashboardStats.active_subscription > 0 && (
              <Card>
                <Grid
                  gridTemplateColumns="auto auto"
                  columnGap="16px"
                  className="dashboard-container__stats__content"
                >
                  <Card className="dashboard-container__stats__icon">
                    <IcProDeliveryTag style={{ fill: '#FF2B85' }} />
                  </Card>
                  <div>
                    <Typography type="label-sm" color="neutral-secondary">
                      {DashboardLabelText.STATS.SUBSCRIBE_EMPLOYEES}
                    </Typography>
                    <Typography type="title-lg" color="neutral-primary">
                      {dashboardStats.active_subscription}
                    </Typography>
                  </div>
                </Grid>
                <Divider />
                <Button
                  key="6"
                  variant="text"
                  layout="full-width-secondary"
                  type="button"
                  onClick={() =>
                    navigate(
                      {
                        pathname: AppRoute.Subscription
                      },
                      { replace: true }
                    )
                  }
                >
                  {DashboardLabelText.STATS.SUBSCRIBE_EMPLOYEES_BUTTON}
                </Button>
              </Card>
            )}
        </Grid>
        <Grid gridTemplateColumns="1fr 1fr" columnGap="24px">
          <Card className="dashboard-spotlight-orders-and-expense">
            {isLoading ? (
              <LoadingAnimation />
            ) : (
              <OrdersAndExpense
                ordersStatsData={data}
                setDatePeriod={(value: string) => {
                  setDatePeriod(value);
                }}
                datePeriod={datePeriod}
                currency={currency}
              />
            )}
          </Card>
          <img
            src={`https://images.deliveryhero.io/image/foodpanda/corporate/Admin%20Portal/${countryCode}/dashboard-highlight-card.png`}
            className="dashboard-container__card-container dashboard-container__image-card"
          />
        </Grid>
      </Grid>
    </Page>
  );
};
